import { API, HOST } from '@/utils/http'
import firebase from 'firebase/compat/app'
import { toast } from "@/components/ui/toast/use-toast"

export const setUser = ({commit}, user) => commit('SET_USER', user)

export const setMember = ({commit}, member) => commit('SET_MEMBER', member)

export const setLanguage = ({commit}, lang) => commit('SET_LANGUAGE', lang)

export const setMainMenuDrawer = ({commit}, isMini) => commit('SET_MAIN_MENU_DRAWER', isMini)

export const changeTheme = ({commit}, isDark) => commit('SET_THEME_COLOR', isDark)

export const setProjectsFilters = ({commit}, showFilters) => commit('SET_PROJECTS_FILTERS', showFilters)

export const updateAvatar = async ({commit}, payload) => {
  try {
    const response = await API({
      headers: {'content-type': 'multipart/form-data'},
      method: 'PATCH',
      url: `${HOST}/member/update-avatar`,
      data: {
        file: payload.file
      }
    })

    commit('SET_MEMBER', response.data)

    toast({ title: 'Avatar updated' })
  } catch (e) {
    throw new Error("Can't update image. " + e)
  }
}

export const logoutFirebase = async ({commit}) => {
  try {
    await firebase.auth().signOut()
    commit('SET_USER', null)
  } catch (e) {
    throw new Error("Can't logout. " + e)
  }
}

export const update = async ({ commit }, payload) => {
  try {
    const response = await API({
      method: 'PATCH',
      url: `${HOST}/member/update`,
      data: {
        display_name: payload.displayName,
        first_name: payload.first_name,
        last_name: payload.last_name
      }
    })

    commit('SET_MEMBER', response.data)
    toast({ title: 'User profile updated' })
  } catch (e) {
    throw new Error("Can't update member. " + e)
  }
}

export const signUpAndCreateOrg = async ({ commit, dispatch }, payload) => {
  try {
    const firebaseToken = await firebase.auth().currentUser.getIdToken(true)

    const response = await API({
      method: 'POST',
      url: `${HOST}/sign-up`,
      data: {
        display_name: payload.display_name,
        org_name: payload.org_name,
        firebase_token: firebaseToken
      }
    })

    localStorage.setItem('accessToken', response.data.access_token)
    localStorage.setItem('refreshToken', response.data.refresh_token)

    const memberResponse = await API({
      method: 'GET',
      url: `${HOST}/member`
    })

    const member = memberResponse.data
    if (member) {
      await dispatch('setMember', member)
      commit('SET_IS_LOGGEDIN', true, { root: true })

      if (window.Sentry && member.email) {
        window.Sentry.setUser({ email: member.email })
      }

      await dispatch('loadInitialData')
    }

    return { success: true, member, tokens: response.data }
  } catch (error) {
    console.error('Sign-up error:', error)

    let errorMessage = 'Failed to create organization'
    if (error.response?.data?.message) {
      errorMessage = error.response.data.message
    } else if (error.response?.data?.detail) {
      errorMessage = error.response.data.detail
    }

    toast({ title: errorMessage, variant: 'destructive' })

    return { success: false, error: errorMessage }
  }
}


export const setupSettings = ({ commit, state }) => {
  const storageSettings = localStorage.getItem('settings')
  if (storageSettings) {
    commit('SET_SETTINGS', JSON.parse(storageSettings))
  } else {
    const getLocation = () => {
      return new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(
          (data) => resolve(data),
          () => reject(null)
        )
      })
    }

    getLocation()
      .then((data) => commit('SET_LOCATION', data))
      .catch(() => commit('SET_LOCATION', null))


    if (Intl) {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone
      commit('SET_TZ', tz)
    }
  }

}

export const saveSettings = ({ state }) => {
  localStorage.setItem('settings', JSON.stringify(state.settings))
}

export const updateLang = ({ commit }, payload) => {
  commit('SET_LANG', payload.language)
}

export const updateTz = ({ commit }, payload) => {
  commit('SET_TZ', payload.tz)
}

export const updateTimeFormat = ({ commit }, payload) => {
  commit('SET_TIME_FORMAT', payload.timeFormat)
}

export const handleAuthentication = async ({ dispatch, commit }, { firebaseToken, inviteCode = null, inviteDetails = null }) => {
  try {
    const tokens = await dispatch('getAuthTokens', { firebaseToken, inviteCode, inviteDetails })
    if (!tokens) {
      return { success: false, reason: 'no_tokens' }
    }

    localStorage.setItem('accessToken', tokens.access_token)
    localStorage.setItem('refreshToken', tokens.refresh_token)

    const response = await API({
      method: 'GET',
      url: `${HOST}/member`
    })

    const member = response.data
    if (!member) {
      return { success: false, reason: 'member_not_found' }
    }

    await dispatch('setMember', member)
    commit('SET_IS_LOGGEDIN', true, { root: true })

    if (window.Sentry && member.email) {
      window.Sentry.setUser({ email: member.email })
    }

    await dispatch('loadInitialData')

    return { success: true, member }
  } catch (error) {
    return { success: false, reason: 'error', error }
  }
}

export const getAuthTokens = async ({ commit }, { firebaseToken, inviteCode = null, inviteDetails = null }) => {
  try {
    let response

    if (inviteCode && inviteDetails) {
      response = await API({
        method: 'POST',
        url: `${HOST}/token/invite/`,
        data: {
          firebase_token: firebaseToken,
          invite_code: inviteCode,
          display_name: inviteDetails.display_name,
          email: inviteDetails.email || null,
          phone: inviteDetails.phone || null
        }
      })
    } else {
      // Regular login
      response = await API({
        method: 'POST',
        url: `${HOST}/token/`,
        data: {
          firebase_token: firebaseToken,
          invite_code: inviteCode
        }
      })
    }

    return response.data
  } catch (error) {
    throw error
  }
}

export const loadInitialData = async ({ dispatch }) => {
  try {
    await dispatch('org/fetchOrgs', null, { root: true })
    await dispatch('project/fetchProjects', null, { root: true })
    await dispatch('project/fetchAllMembers', null, { root: true })
    await dispatch('project/fetchAllTags', null, { root: true })
    await dispatch('project/fetchAllInvites', null, { root: true })
    return true
  } catch (error) {
    console.error('Error loading initial data:', error)
    throw error
  }
}
